/**
 * TAHLEEL.ai Registration Page Component
 *
 * Purpose: New user registration for coaches
 * Framework: React
 * Authentication: Supabase user creation with email verification
 */

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Crown, Mail, Lock, User, Building, Shield, CheckCircle, AlertCircle } from 'lucide-react';
import { supabase } from '../services/api';

const RegisterPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  
  // Form state
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    organizationName: '',
    teamName: '',
    role: 'coach',
    league: ''
  });
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);

  /**
   * Calculate password strength
   */
  const calculatePasswordStrength = (password) => {
    let strength = 0;
    
    if (password.length >= 8) strength += 25;
    if (password.length >= 12) strength += 25;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength += 25;
    if (/\d/.test(password)) strength += 15;
    if (/[^A-Za-z0-9]/.test(password)) strength += 10;
    
    return Math.min(strength, 100);
  };

  /**
   * Handle input changes
   */
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Update password strength for password field
    if (name === 'password') {
      setPasswordStrength(calculatePasswordStrength(value));
    }
  };

  /**
   * Validate form inputs
   */
  const validateForm = () => {
    // Email validation
    if (!formData.email || !formData.email.includes('@')) {
      throw new Error('Please enter a valid email address');
    }

    // Password validation
    if (!formData.password || formData.password.length < 8) {
      throw new Error('Password must be at least 8 characters long');
    }

    if (formData.password !== formData.confirmPassword) {
      throw new Error('Passwords do not match');
    }

    // Name validation
    if (!formData.firstName || !formData.lastName) {
      throw new Error('First name and last name are required');
    }

    // Organization validation
    if (!formData.organizationName) {
      throw new Error('Organization name is required');
    }

    return true;
  };

  /**
   * Handle registration form submission
   */
  const handleRegister = async (e) => {
    e.preventDefault();
    
    // Reset messages
    setError('');
    setSuccess(false);
    setLoading(true);

    try {
      // Validate form
      validateForm();

      // Register user with Supabase
      const { data, error: signUpError } = await supabase.auth.signUp({
        email: formData.email.trim(),
        password: formData.password,
        options: {
          data: {
            first_name: formData.firstName.trim(),
            last_name: formData.lastName.trim(),
            organization_name: formData.organizationName.trim(),
            team_name: formData.teamName.trim(),
            role: formData.role,
            league: formData.league,
            user_type: 'Elite Coach',
            preferred_language: i18n.language
          }
        }
      });

      if (signUpError) {
        throw signUpError;
      }

      if (!data.user) {
        throw new Error('Registration failed. Please try again.');
      }

      console.log('✅ Registration successful:', data.user.email);

      // Show success message
      setSuccess(true);

      // Auto-redirect after 5 seconds
      setTimeout(() => {
        navigate('/login');
      }, 5000);

    } catch (err) {
      console.error('❌ Registration error:', err);
      
      // User-friendly error messages
      let errorMessage = 'Registration failed. Please try again.';
      
      if (err.message.includes('User already registered')) {
        errorMessage = 'An account with this email already exists. Please login instead.';
      } else if (err.message.includes('Password should be')) {
        errorMessage = 'Password is too weak. Please use a stronger password.';
      } else if (err.message.includes('network')) {
        errorMessage = 'Network error. Please check your internet connection.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Get password strength color
   */
  const getPasswordStrengthColor = () => {
    if (passwordStrength < 40) return 'bg-red-500';
    if (passwordStrength < 70) return 'bg-yellow-500';
    return 'bg-emerald-500';
  };

  /**
   * Get password strength label
   */
  const getPasswordStrengthLabel = () => {
    if (passwordStrength < 40) return 'Weak';
    if (passwordStrength < 70) return 'Medium';
    return 'Strong';
  };

  // If registration successful, show success screen
  if (success) {
    return (
      <div 
        className="min-h-screen relative flex items-center justify-center px-4 py-12"
        style={{
          backgroundImage: 'url(/images/sports-ai-background.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        {/* LUXURY OVERLAY */}
        <div 
          className="absolute inset-0 bg-gradient-to-br from-royal-900 via-royal-800 to-royal-900"
          style={{ opacity: 0.92 }}
        />
        <div className="absolute inset-0 bg-stadium-pattern opacity-5" />

        {/* SUCCESS CARD */}
        <div className="relative max-w-md w-full z-10">
          <div className="bg-white bg-opacity-95 backdrop-filter backdrop-blur-xl rounded-2xl shadow-luxury p-8 text-center border-2 border-gold-500">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-gold rounded-2xl mb-6 shadow-gold-glow">
              <CheckCircle className="h-12 w-12 text-royal-900" />
            </div>
            
            <h2 className="text-4xl font-display font-black text-royal-900 mb-4">
              {t('auth.welcomeBack')}
            </h2>
            
            <div className="bg-emerald-50 border-2 border-emerald-300 rounded-xl p-5 mb-6">
              <p className="text-emerald-900 font-bold mb-2">
                {t('common.success')}
              </p>
              <p className="text-emerald-800 text-sm font-semibold mb-3">
                We've sent a verification email to:
              </p>
              <p className="text-royal-900 font-black text-lg">
                {formData.email}
              </p>
            </div>

            <div className="space-y-3 text-left rtl:text-right mb-6 bg-royal-50 rounded-xl p-5 border-2 border-royal-200">
              <div className="flex items-start">
                <div className="w-7 h-7 bg-gradient-gold rounded-lg flex items-center justify-center mr-3 rtl:ml-3 rtl:mr-0 flex-shrink-0">
                  <span className="text-royal-900 font-black text-sm">1</span>
                </div>
                <p className="text-royal-900 text-sm font-semibold pt-1">
                  Check your email inbox (and spam folder)
                </p>
              </div>
              <div className="flex items-start">
                <div className="w-7 h-7 bg-gradient-gold rounded-lg flex items-center justify-center mr-3 rtl:ml-3 rtl:mr-0 flex-shrink-0">
                  <span className="text-royal-900 font-black text-sm">2</span>
                </div>
                <p className="text-royal-900 text-sm font-semibold pt-1">
                  Click the verification link in the email
                </p>
              </div>
              <div className="flex items-start">
                <div className="w-7 h-7 bg-gradient-gold rounded-lg flex items-center justify-center mr-3 rtl:ml-3 rtl:mr-0 flex-shrink-0">
                  <span className="text-royal-900 font-black text-sm">3</span>
                </div>
                <p className="text-royal-900 text-sm font-semibold pt-1">
                  Return here and login to your elite account
                </p>
              </div>
            </div>

            <button
              onClick={() => navigate('/login')}
              className="w-full py-4 px-6 bg-gradient-gold hover:shadow-gold-glow text-royal-900 font-black text-lg rounded-xl transition-all shadow-lg transform hover:scale-105"
            >
              {t('auth.login')}
            </button>

            <p className="text-gray-600 text-xs mt-4 font-semibold">
              Redirecting automatically in 5 seconds...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen relative flex items-center justify-center px-4 py-12"
      style={{
        backgroundImage: 'url(/images/sports-ai-background.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }}
    >
      {/* LUXURY OVERLAY */}
      <div 
        className="absolute inset-0 bg-gradient-to-br from-royal-900 via-royal-800 to-royal-900"
        style={{ opacity: 0.92 }}
      />
      <div className="absolute inset-0 bg-stadium-pattern opacity-5" />

      {/* CONTENT */}
      <div className="relative max-w-2xl w-full z-10">
        {/* LUXURY LOGO AND HEADER */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-gold rounded-2xl mb-4 shadow-gold-glow transform hover:scale-110 transition-all">
            <Crown className="h-12 w-12 text-royal-900" />
          </div>
          <h1 className="text-4xl font-display font-black text-white mb-2 flex items-center justify-center space-x-2 rtl:space-x-reverse">
            <span>{t('auth.register')}</span>
            <span className="text-gold-400">{t('app.name')}</span>
          </h1>
          <div className="h-1 w-24 bg-gradient-gold mx-auto mb-3 rounded-full"></div>
          <p className="text-royal-200 text-lg font-bold">
            {t('auth.createAccount')}
          </p>
        </div>

        {/* Language Switcher */}
        <div className="flex justify-center space-x-2 rtl:space-x-reverse mb-6">
          <button
            onClick={() => i18n.changeLanguage('en')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              i18n.language === 'en'
                ? 'bg-gold-500 text-white'
                : 'bg-white/10 text-white hover:bg-white/20'
            }`}
          >
            English
          </button>
          <button
            onClick={() => i18n.changeLanguage('ar')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              i18n.language === 'ar'
                ? 'bg-gold-500 text-white'
                : 'bg-white/10 text-white hover:bg-white/20'
            }`}
          >
            العربية
          </button>
        </div>

        {/* LUXURY REGISTRATION CARD */}
        <div 
          className="bg-white bg-opacity-95 backdrop-filter backdrop-blur-xl rounded-2xl shadow-luxury p-8 border-2 border-gold-500"
          style={{ backdropFilter: 'blur(20px)' }}
        >
          <div className="text-center mb-6">
            <h2 className="text-3xl font-display font-black text-royal-900 mb-2">
              {t('auth.createAccount')}
            </h2>
            <p className="text-gray-600 font-semibold">Join the future of tactical analysis</p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border-2 border-red-300 rounded-xl">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-red-500 rounded-lg flex items-center justify-center">
                    <AlertCircle className="h-5 w-5 text-white" />
                  </div>
                </div>
                <div className="ml-3 rtl:mr-3 rtl:ml-0">
                  <p className="text-red-900 font-bold text-sm">Registration Failed</p>
                  <p className="text-red-700 text-sm mt-1">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Registration Form */}
          <form onSubmit={handleRegister} className="space-y-6">
            {/* Personal Information Section */}
            <div>
              <div className="flex items-center space-x-2 rtl:space-x-reverse mb-4">
                <User className="h-5 w-5 text-gold-600" />
                <h3 className="text-lg font-black text-royal-900">Personal Information</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* First Name */}
                <div>
                  <label 
                    htmlFor="firstName" 
                    className="block text-sm font-bold text-royal-900 mb-2"
                  >
                    {t('auth.fullName').split(' ')[0]} *
                  </label>
                  <input
                    id="firstName"
                    name="firstName"
                    type="text"
                    value={formData.firstName}
                    onChange={handleChange}
                    disabled={loading}
                    placeholder="Ahmed"
                    className="w-full px-4 py-3 border-2 border-sand-300 rounded-xl focus:ring-2 focus:ring-gold-500 focus:border-gold-500 disabled:bg-sand-100 disabled:cursor-not-allowed font-semibold text-royal-900 placeholder-gray-400"
                    required
                  />
                </div>

                {/* Last Name */}
                <div>
                  <label 
                    htmlFor="lastName" 
                    className="block text-sm font-bold text-royal-900 mb-2"
                  >
                    Last Name *
                  </label>
                  <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    value={formData.lastName}
                    onChange={handleChange}
                    disabled={loading}
                    placeholder="Al-Mansour"
                    className="w-full px-4 py-3 border-2 border-sand-300 rounded-xl focus:ring-2 focus:ring-gold-500 focus:border-gold-500 disabled:bg-sand-100 disabled:cursor-not-allowed font-semibold text-royal-900 placeholder-gray-400"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Email */}
            <div>
              <label 
                htmlFor="email" 
                className="block text-sm font-bold text-royal-900 mb-2"
              >
                {t('auth.email')} *
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 rtl:left-auto rtl:right-0 pl-4 rtl:pr-4 rtl:pl-0 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-royal-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={loading}
                  placeholder={t('auth.enterEmail')}
                  className="w-full pl-12 rtl:pr-12 rtl:pl-4 pr-4 py-3 border-2 border-sand-300 rounded-xl focus:ring-2 focus:ring-gold-500 focus:border-gold-500 disabled:bg-sand-100 disabled:cursor-not-allowed font-semibold text-royal-900 placeholder-gray-400"
                  required
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label 
                htmlFor="password" 
                className="block text-sm font-bold text-royal-900 mb-2"
              >
                {t('auth.password')} *
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 rtl:left-auto rtl:right-0 pl-4 rtl:pr-4 rtl:pl-0 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-royal-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  disabled={loading}
                  placeholder={t('auth.enterPassword')}
                  className="w-full pl-12 rtl:pr-12 rtl:pl-4 pr-4 py-3 border-2 border-sand-300 rounded-xl focus:ring-2 focus:ring-gold-500 focus:border-gold-500 disabled:bg-sand-100 disabled:cursor-not-allowed font-semibold text-royal-900"
                  required
                  minLength={8}
                />
              </div>
              
              {/* Password Strength Indicator */}
              {formData.password && (
                <div className="mt-3 bg-sand-50 rounded-lg p-3 border border-sand-300">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold text-gray-700">Password Strength:</span>
                    <span className={`text-xs font-black ${
                      passwordStrength < 40 ? 'text-red-600' : 
                      passwordStrength < 70 ? 'text-yellow-600' : 'text-emerald-600'
                    }`}>
                      {getPasswordStrengthLabel()}
                    </span>
                  </div>
                  <div className="h-2.5 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className={`h-full ${getPasswordStrengthColor()} transition-all duration-300`}
                      style={{ width: `${passwordStrength}%` }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label 
                htmlFor="confirmPassword" 
                className="block text-sm font-bold text-royal-900 mb-2"
              >
                {t('auth.confirmPassword')} *
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 rtl:left-auto rtl:right-0 pl-4 rtl:pr-4 rtl:pl-0 flex items-center pointer-events-none">
                  <Shield className="h-5 w-5 text-royal-400" />
                </div>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  disabled={loading}
                  placeholder={t('auth.enterPassword')}
                  className="w-full pl-12 rtl:pr-12 rtl:pl-4 pr-4 py-3 border-2 border-sand-300 rounded-xl focus:ring-2 focus:ring-gold-500 focus:border-gold-500 disabled:bg-sand-100 disabled:cursor-not-allowed font-semibold text-royal-900"
                  required
                />
              </div>
            </div>

            {/* Organization Information Section */}
            <div className="pt-6 border-t-2 border-gold-200">
              <div className="flex items-center space-x-2 rtl:space-x-reverse mb-4">
                <Building className="h-5 w-5 text-gold-600" />
                <h3 className="text-lg font-black text-royal-900">Organization Details</h3>
              </div>

              <div className="space-y-4">
                {/* Organization Name */}
                <div>
                  <label 
                    htmlFor="organizationName" 
                    className="block text-sm font-bold text-royal-900 mb-2"
                  >
                    Organization/Club Name *
                  </label>
                  <input
                    id="organizationName"
                    name="organizationName"
                    type="text"
                    value={formData.organizationName}
                    onChange={handleChange}
                    disabled={loading}
                    placeholder="Al Nassr FC"
                    className="w-full px-4 py-3 border-2 border-sand-300 rounded-xl focus:ring-2 focus:ring-gold-500 focus:border-gold-500 disabled:bg-sand-100 disabled:cursor-not-allowed font-semibold text-royal-900 placeholder-gray-400"
                    required
                  />
                </div>

                {/* Team Name */}
                <div>
                  <label 
                    htmlFor="teamName" 
                    className="block text-sm font-bold text-royal-900 mb-2"
                  >
                    Team Name ({t('upload.optional')})
                  </label>
                  <input
                    id="teamName"
                    name="teamName"
                    type="text"
                    value={formData.teamName}
                    onChange={handleChange}
                    disabled={loading}
                    placeholder="First Team"
                    className="w-full px-4 py-3 border-2 border-sand-300 rounded-xl focus:ring-2 focus:ring-gold-500 focus:border-gold-500 disabled:bg-sand-100 disabled:cursor-not-allowed font-semibold text-royal-900 placeholder-gray-400"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Role */}
                  <div>
                    <label 
                      htmlFor="role" 
                      className="block text-sm font-bold text-royal-900 mb-2"
                    >
                      Role *
                    </label>
                    <select
                      id="role"
                      name="role"
                      value={formData.role}
                      onChange={handleChange}
                      disabled={loading}
                      className="w-full px-4 py-3 border-2 border-sand-300 rounded-xl focus:ring-2 focus:ring-gold-500 focus:border-gold-500 disabled:bg-sand-100 disabled:cursor-not-allowed font-semibold text-royal-900"
                    >
                      <option value="coach">Head Coach</option>
                      <option value="assistant_coach">Assistant Coach</option>
                      <option value="tactical_analyst">Tactical Analyst</option>
                      <option value="director">Technical Director</option>
                    </select>
                  </div>

                  {/* League */}
                  <div>
                    <label 
                      htmlFor="league" 
                      className="block text-sm font-bold text-royal-900 mb-2"
                    >
                      League *
                    </label>
                    <select
                      id="league"
                      name="league"
                      value={formData.league}
                      onChange={handleChange}
                      disabled={loading}
                      className="w-full px-4 py-3 border-2 border-sand-300 rounded-xl focus:ring-2 focus:ring-gold-500 focus:border-gold-500 disabled:bg-sand-100 disabled:cursor-not-allowed font-semibold text-royal-900"
                    >
                      <option value="">Select a league</option>
                      <option value="Saudi Pro League">Saudi Pro League</option>
                      <option value="UAE Pro League">UAE Pro League</option>
                      <option value="Qatar Stars League">Qatar Stars League</option>
                      <option value="Egyptian Premier League">Egyptian Premier League</option>
                      <option value="Other">Other League</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Terms and Conditions */}
            <div className="flex items-start bg-royal-50 rounded-xl p-4 border-2 border-royal-200">
              <input
                id="terms"
                type="checkbox"
                required
                className="h-5 w-5 text-gold-600 focus:ring-gold-500 border-gray-300 rounded mt-0.5"
              />
              <label htmlFor="terms" className="ml-3 rtl:mr-3 rtl:ml-0 block text-sm text-royal-900 font-semibold">
                I agree to the{' '}
                <Link to="/terms" className="text-gold-600 hover:text-gold-700 font-black underline">
                  Terms of Service
                </Link>
                {' '}and{' '}
                <Link to="/privacy" className="text-gold-600 hover:text-gold-700 font-black underline">
                  Privacy Policy
                </Link>
              </label>
            </div>

            {/* Register Button */}
            <button
              type="submit"
              disabled={loading}
              className={`w-full py-4 px-6 rounded-xl font-black text-lg transition-all transform ${
                loading 
                  ? 'bg-sand-400 cursor-not-allowed text-sand-600' 
                  : 'bg-gradient-gold hover:shadow-gold-glow text-royal-900 shadow-lg hover:scale-105'
              }`}
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin h-5 w-5 mr-3 rtl:ml-3 rtl:mr-0" viewBox="0 0 24 24">
                    <circle 
                      className="opacity-25" 
                      cx="12" 
                      cy="12" 
                      r="10" 
                      stroke="currentColor" 
                      strokeWidth="4"
                      fill="none"
                    />
                    <path 
                      className="opacity-75" 
                      fill="currentColor" 
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  {t('auth.creatingAccount')}
                </span>
              ) : (
                t('auth.createAccount')
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="mt-8 mb-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t-2 border-sand-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-600 font-bold">
                  {t('auth.hasAccount')}
                </span>
              </div>
            </div>
          </div>

          {/* Login Link */}
          <div className="text-center">
            <Link 
              to="/login" 
              className="inline-flex items-center space-x-2 rtl:space-x-reverse text-gold-600 hover:text-gold-700 font-bold text-lg"
            >
              <span>{t('auth.signIn')}</span>
              <span>→</span>
            </Link>
          </div>
        </div>

        {/* Copyright */}
        <div className="mt-8 text-center text-gold-300 text-xs font-semibold">
          <p>&copy; 2025 TAHLEEL.ai by Auwire Technologies. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
